import { useEffect, useMemo, useState } from 'react'
import { useRouter } from 'next/router'
import clsx from 'clsx'
import { publicConfig } from 'config'
import { fetchCommunityCounts } from 'lib/dealhunt/client'
import { brandedTitle, productSlug } from 'lib/dealhunt/url'
import { sharedMe } from 'lib/owner-notes/api'
import { hasDeviceMarker, useDeclaredProduct, useDeviceEpoch } from 'lib/owner-notes/declared'
import { useOwnTypeahead } from 'lib/owner-notes/own-typeahead'
import {
  gearRows,
  hitInScope,
  hitNodeId,
  newReviewHref,
  pickerCountLine,
  productSlugParam,
  reviewAward,
  type GearRow,
} from 'lib/community/model'
import type { CommunityCountsResponse } from 'lib/community/types'
import type { DealHuntSuggestHit, OwnerShelfItem } from 'lib/dealhunt/types'
import { shelfSlug } from 'lib/community/writer-handoff'
import styles from './OwnPicker.module.css'

interface OwnPickerProps {
  /** Unique per mount: the listbox id. */
  id: string
  tone?: 'light' | 'ink'
  autoFocus?: boolean
  onPick?: (hit: DealHuntSuggestHit) => void
  /** A category's `fullSlug` prefix (`pickerScope`): matches outside it are dropped. */
  scope?: string
  /** The category's name, for the empty state ("No Headphones match"). */
  scopeName?: string
  /** Inside a narrow side card (the category rail's write card, `CategoryRail`): the results sit in flow (the card grows) and each
   *  row is two lines. The popover and the one-line row are for full-width panels only. */
  compact?: boolean
  /** In a sheet (the phone's bottom-bar picker): the results flow under the field instead of
   *  floating over the page — a floating list would be clipped by the sheet's own scroll. */
  inline?: boolean
  /** Where a pick leads (default: the community form for that product, `newReviewHref`). The
   *  form's own "Change" keeps its handoff params (`writerHref`). */
  hrefFor?: (slug: string) => string
}

/** The SoundGuys slug of a typeahead hit; null for a hit without a taxonomy path. */
function hitSlug(hit: DealHuntSuggestHit): string | null {
  return hit.fullSlug ? productSlugParam(productSlug(hit.brand, hit.fullSlug, hit.brandSlug)) : null
}

/**
 * Published-review counts and the points table for a set of node ids, read once per set (the
 * request is sorted, so the same set is the same cached URL). Null until it answers, and on any
 * failure — an older DealHunt answers its HTML shell: the rows then show no count and no points.
 */
function useCounts(nodeIds: string[]): CommunityCountsResponse | null {
  const key = Array.from(new Set(nodeIds)).sort().join(',')
  const [answer, setAnswer] = useState<{ key: string; counts: CommunityCountsResponse } | null>(
    null
  )
  useEffect(() => {
    if (!key) {
      return undefined
    }
    const ctrl = new AbortController()
    fetchCommunityCounts(key.split(','), ctrl.signal).then(
      counts => counts && setAnswer({ key, counts }),
      () => undefined
    )
    return () => ctrl.abort()
  }, [key])
  return answer && answer.key === key ? answer.counts : null
}

/** "Your gear": what the reader can review now (`gearRows`) — the declared product without a
 *  note of theirs, shelf products whose note was rejected. `/me` only on a browser that holds an
 *  owner device (as `YouCard`). */
function useGear(): GearRow[] {
  const { declared } = useDeclaredProduct()
  const epoch = useDeviceEpoch()
  const [shelf, setShelf] = useState<OwnerShelfItem[]>([])
  useEffect(() => {
    if (!hasDeviceMarker()) {
      setShelf([])
      return undefined
    }
    let live = true
    sharedMe().then(
      me => live && setShelf(me.shelf),
      () => undefined
    )
    return () => {
      live = false
    }
  }, [epoch])
  return useMemo(() => gearRows(declared, shelf, shelfSlug), [declared, shelf])
}

interface RowProps {
  name: string
  imageUrl: string | null
  count: number | null
  counts: CommunityCountsResponse | null
  compact: boolean
}

/** One row's face (design 5a): thumbnail, name, review count, points pill, "Review". Compact:
 *  thumbnail + name on line 1; count · pill on the left and "Review" on the right on line 2. */
const RowBody: React.VFC<RowProps> = ({ name, imageUrl, count, counts, compact }) => {
  const line = pickerCountLine(count, counts?.points ?? null)
  const award = counts ? reviewAward(counts.points, count) : null
  const thumb = (
    <span className={styles.hitThumb}>
      {imageUrl && (
        // eslint-disable-next-line @next/next/no-img-element
        <img src={imageUrl} alt="" loading="lazy" />
      )}
    </span>
  )
  const pill = award && (
    <span className={clsx(styles.pill, { [styles.pillBonus]: award.bonus })}>
      {`+${award.points}`}
    </span>
  )
  const review = (
    <span className={styles.review} aria-hidden="true">
      {'Review'}
    </span>
  )
  if (compact) {
    return (
      <>
        <span className={styles.compactTop}>
          {thumb}
          <span className={styles.compactName}>{name}</span>
        </span>
        <span className={styles.compactMeta}>
          <span className={styles.compactInfo}>
            {line}
            {line && pill && ' · '}
            {pill}
          </span>
          {review}
        </span>
      </>
    )
  }
  return (
    <>
      {thumb}
      <span className={styles.hitText}>
        <span className={styles.hitName}>{name}</span>
        {line && <span className={styles.hitCount}>{line}</span>}
      </span>
      <span className={styles.hitEnd}>
        {pill}
        {review}
      </span>
    </>
  )
}

/** The count DealHunt sent for a node, or null when it did not say (unknown ≠ zero). */
function countOf(counts: CommunityCountsResponse | null, nodeId: string | null): number | null {
  if (!counts || !nodeId || !Object.prototype.hasOwnProperty.call(counts.counts, nodeId)) {
    return null
  }
  return counts.counts[nodeId]
}

/**
 * "Which product do you own?" on the community pages (design 5a). Two sections: "Your gear" —
 * what the reader can review now (the declared product, shelf products whose note was rejected) —
 * and "Matches", the same typeahead as the category capture band (`useOwnTypeahead`). Each row
 * says how many owner reviews the product has and what a review there earns (DealHunt's points
 * table: `review`, plus `first_review_bonus` while it has none). A pick opens the community
 * review form for that product.
 *
 * Keyboard: the arrows and Enter walk "Matches" only, in the hook's own order — the rendered
 * options ARE `results`, unfiltered, so the highlight index stays aligned. "Your gear" rows are
 * plain buttons, reached with Tab.
 */
const OwnPicker: React.VFC<OwnPickerProps> = ({
  id,
  tone = 'light',
  autoFocus = false,
  onPick,
  scope,
  scopeName,
  compact = false,
  inline = false,
  hrefFor,
}) => {
  const router = useRouter()
  const trailingSlash = publicConfig('useTrailingSlash')
  const gear = useGear()
  const go = (slug: string): void => {
    void router.push(hrefFor ? hrefFor(slug) : newReviewHref(slug, trailingSlash))
  }
  const typeahead = useOwnTypeahead(
    hit => {
      // A hit without a taxonomy slug has no product to write the review on.
      const slug = hitSlug(hit)
      if (!slug) {
        return
      }
      onPick?.(hit)
      go(slug)
    },
    undefined,
    scope ? hit => hitInScope(hit, scope) : undefined
  )
  const { showDropdown, results, highlight, loading, trimmed, open } = typeahead
  const gearCounts = useCounts(open ? gear.flatMap(row => (row.nodeId ? [row.nodeId] : [])) : [])
  const hitCounts = useCounts(results.flatMap(hit => hitNodeId(hit) ?? []))
  const showGear = open && gear.length > 0
  const groupId = `${id}-gear`
  const emptyLine = scopeName
    ? `No ${scopeName} match “${trimmed}”.`
    : `Nothing matches “${trimmed}”.`

  return (
    <div
      ref={typeahead.rootRef}
      className={clsx(styles.root, styles[`tone_${tone}`], {
        [styles.compact]: compact,
        [styles.inline]: inline,
      })}
    >
      <input
        type="text"
        className={styles.input}
        role="combobox"
        aria-expanded={showDropdown}
        aria-controls={id}
        aria-label="Which product do you own?"
        placeholder="Search the product you own…"
        autoComplete="off"
        // eslint-disable-next-line jsx-a11y/no-autofocus -- opened by the reader's own click
        autoFocus={autoFocus}
        value={typeahead.query}
        onFocus={() => typeahead.setOpen(true)}
        onChange={e => {
          typeahead.setQuery(e.target.value)
          typeahead.setOpen(true)
        }}
        onKeyDown={typeahead.onKeyDown}
      />
      {(showGear || showDropdown) && (
        <div className={styles.dropdown}>
          {showGear && (
            <div role="group" aria-labelledby={groupId} className={styles.section}>
              <p id={groupId} className={styles.sectionTitle}>
                {'Your gear'}
              </p>
              {gear.map(row => (
                <button
                  key={row.slug}
                  type="button"
                  className={styles.hit}
                  onClick={() => {
                    typeahead.setOpen(false)
                    go(row.slug)
                  }}
                >
                  <RowBody
                    name={row.name}
                    imageUrl={row.imageUrl}
                    count={countOf(gearCounts, row.nodeId)}
                    counts={gearCounts}
                    compact={compact}
                  />
                </button>
              ))}
            </div>
          )}
          {showDropdown && (
            <div className={styles.section}>
              <p id={`${id}-title`} className={styles.sectionTitle}>
                {'Matches'}
              </p>
              <div id={id} role="listbox" aria-labelledby={`${id}-title`}>
                {results.map((hit, i) => (
                  <button
                    key={hit.objectID}
                    type="button"
                    role="option"
                    aria-selected={i === highlight}
                    className={clsx(styles.hit, { [styles.hitOn]: i === highlight })}
                    onMouseEnter={() => typeahead.setHighlight(i)}
                    onClick={() => typeahead.select(hit)}
                  >
                    <RowBody
                      name={brandedTitle(hit.brand, hit.canonicalName || hit.title)}
                      imageUrl={hit.cloudinaryImageUrl || hit.imageUrl}
                      count={countOf(hitCounts, hitNodeId(hit))}
                      counts={hitCounts}
                      compact={compact}
                    />
                  </button>
                ))}
                {!results.length && (
                  <p className={styles.notice}>{loading ? 'Searching…' : emptyLine}</p>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default OwnPicker
